<script setup lang="ts">
import { onBeforeUnmount, onMounted } from 'vue';

import type { Manifest } from '@clovyr/pollen/manifest/manifest';

import CTASection from '@/components/ai-landing/CTASection.vue';
import FeaturesSection from '@/components/ai-landing/FeaturesSection.vue';
import FooterSection from '@/components/ai-landing/FooterSection.vue';
import HeroSection from '@/components/ai-landing/HeroSection.vue';
import HowItWorksSection from '@/components/ai-landing/HowItWorksSection.vue';
import IntegrationsSection from '@/components/ai-landing/IntegrationsSection.vue';
import PrivacySection from '@/components/ai-landing/PrivacySection.vue';
import SectionDivider from '@/components/ai-landing/SectionDivider.vue';
import TrustBarSection from '@/components/ai-landing/TrustBarSection.vue';
import { useLaunchButton } from '@/composables/useLaunchButton';
import Modals from '@/global/Modals.vue';
import AppLauncher from '@/views/AppLauncher/AppLauncher.vue';

import dividerDesk from '../../assets/images/ai-landing/divider-desk.jpg';
import dividerInfra from '../../assets/images/ai-landing/divider-infra.jpg';
import dividerLaptop from '../../assets/images/ai-landing/divider-laptop.jpg';
import dividerSecurity from '../../assets/images/ai-landing/divider-security.jpg';
import dividerStreaks from '../../assets/images/ai-landing/divider-streaks.jpg';

const props = defineProps<{
  app: Manifest;
  launch?: boolean;
}>();

const router = useRouter();

void toRef(props.app, 'metadata');

const showInlineLauncher = computed(() => !!props.launch);

const openInlineLauncher = () => {
  void router.push({ path: '/ai/launch' });
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const closeInlineLauncher = () => {
  void router.push({ path: '/ai' });
};

const { handleLaunchClicked } = useLaunchButton(props.app, {
  onAuthorizedLaunch: openInlineLauncher,
});

const dividers = {
  desk: dividerDesk,
  infra: dividerInfra,
  streaks: dividerStreaks,
  security: dividerSecurity,
  laptop: dividerLaptop,
};

onMounted(() => {
  document.documentElement.classList.add('ai-landing-root');
});

onBeforeUnmount(() => {
  document.documentElement.classList.remove('ai-landing-root');
});
</script>

<template>
  <div class="ai-landing-page" data-page="ai-landing">
    <HeroSection :app="app" :on-launch="handleLaunchClicked" :launcher="showInlineLauncher">
      <template #launcher>
        <section class="ai-landing-launcher">
          <AppLauncher :app="app" parent-route="/ai" embedded @close="closeInlineLauncher" />
        </section>
      </template>
    </HeroSection>

    <template v-if="!showInlineLauncher">
      <TrustBarSection />
      <FeaturesSection />
      <SectionDivider :image-url="dividers.desk" from-color="#221a33" to-color="#221a33" />
      <IntegrationsSection />
      <SectionDivider :image-url="dividers.infra" from-color="#221a33" to-color="#1e1530" />
      <HowItWorksSection />
      <SectionDivider :image-url="dividers.streaks" from-color="#1e1530" to-color="#221a33" />
      <PrivacySection />
      <!-- <SectionDivider :image-url="dividers.security" from-color="#221a33" to-color="#1e1530" /> -->
      <!-- <PricingSection /> -->
      <SectionDivider :image-url="dividers.laptop" from-color="#1e1530" to-color="#221a33" />
      <CTASection :app="app" :on-launch="handleLaunchClicked" />
      <FooterSection />
    </template>

    <Modals />
  </div>
</template>

<style scoped lang="scss">
:global(html.ai-landing-root) {
  font-size: 16px !important;
}

.ai-landing-page {
  min-height: 100vh;
  background: #221a33;
  color: #fff;
  font-size: 16px;
  line-height: 1.5;
  font-family:
    ui-sans-serif,
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    sans-serif,
    'Apple Color Emoji',
    'Segoe UI Emoji',
    'Segoe UI Symbol';
  font-weight: 400;
  letter-spacing: 0;
  font-kerning: normal;
  font-feature-settings: 'kern' 1;
  text-size-adjust: 100%;
  text-size-adjust: 100%;
}

.ai-landing-page :deep(.ai-hero),
.ai-landing-page :deep(.ai-trust),
.ai-landing-page :deep(.ai-features),
.ai-landing-page :deep(.ai-integrations),
.ai-landing-page :deep(.ai-how),
.ai-landing-page :deep(.ai-privacy),
.ai-landing-page :deep(.ai-pricing),
.ai-landing-page :deep(.ai-cta),
.ai-landing-page :deep(.ai-footer),
.ai-landing-page :deep(.ai-testimonials) {
  font-family: inherit;
  font-weight: inherit;
  letter-spacing: inherit;
  font-size: 1rem;
}

.ai-landing-page :deep(.ai-hero *),
.ai-landing-page :deep(.ai-trust *),
.ai-landing-page :deep(.ai-features *),
.ai-landing-page :deep(.ai-integrations *),
.ai-landing-page :deep(.ai-how *),
.ai-landing-page :deep(.ai-privacy *),
.ai-landing-page :deep(.ai-pricing *),
.ai-landing-page :deep(.ai-cta *),
.ai-landing-page :deep(.ai-footer *),
.ai-landing-page :deep(.ai-testimonials *) {
  font-family: inherit;
}

.ai-landing-launcher {
  --lw-inline-pad-x: 2rem;
  --lw-hero-pad-y: 1.5rem;
  --lw-footer-pad-y: 1rem;
  --lw-card-radius: 1.75rem;

  width: 100%;
  max-width: 1240px;
  margin: 0 auto;
  padding: 3.5rem var(--lw-inline-pad-x) 4rem;
  box-sizing: border-box;
  flex: 1;
}

/*
 * The wizard "card" itself: a translucent, softly-bordered surface so the
 * background artwork can subtly influence it instead of reading as a flat,
 * fully opaque rectangle sitting on top of the hero image.
 */
.ai-landing-launcher :deep(.launcher-inline) {
  position: relative;
  isolation: isolate;
  background: linear-gradient(
    180deg,
    rgb(40, 31, 59, 92%) 0%,
    rgb(30, 23, 46, 95%) 55%,
    rgb(24, 18, 38, 97%) 100%
  );
  color: #fff;
  border: 1px solid rgb(255, 255, 255, 10%);
  border-radius: var(--lw-card-radius);
  padding-bottom: 8.25rem;
  box-shadow:
    0 32px 64px -32px rgb(8, 5, 16, 65%),
    0 1px 0 rgb(255, 255, 255, 5%) inset;
}

/*
 * Faint top glow so the edge fades into the background rather than cutting it
 * off abruptly. `overflow` is intentionally left visible on the card so that
 * dropdowns/tooltips inside the wizard (host & region pickers, etc.) are not
 * clipped; the glow uses its own radius instead of relying on clipping.
 */
.ai-landing-launcher :deep(.launcher-inline)::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  border-radius: inherit;
  background: radial-gradient(120% 55% at 50% 0%, rgb(255, 255, 255, 6%), transparent 62%);
}

.ai-landing-launcher :deep(.launcher-inline) > * {
  position: relative;
  z-index: 1;
}

.ai-landing-launcher :deep(.app-launcher-hero) {
  padding: var(--lw-hero-pad-y) var(--lw-inline-pad-x) 1.75rem;
}

.ai-landing-launcher :deep(.app-launcher-hero__app-icon) {
  display: none;
}

.ai-landing-launcher :deep(.hero-masthead__right .image-media) {
  transform: none !important;
  height: 6.5rem !important;
}

.ai-landing-launcher :deep(.hero-masthead__right) {
  align-self: flex-start;
  flex-shrink: 0;
  margin-top: 0.25rem;
}

.ai-landing-launcher :deep(.app-launcher-hero .hero-masthead) {
  gap: 1.5rem;
  align-items: flex-start;
}

.ai-landing-launcher :deep(.app-launcher-hero .hero-heading) {
  padding-top: 0;
  padding-left: 0;
}

.ai-landing-launcher :deep(.launch-setup__main),
.ai-landing-launcher :deep(.review-setup__main),
.ai-landing-launcher :deep(.choose-host__main),
.ai-landing-launcher :deep(.billing-period) {
  max-width: 1360px;
  margin: 0 auto;
}

.ai-landing-launcher :deep(.launch-setup .row),
.ai-landing-launcher :deep(.review-setup .row) {
  --cl-gutter-x: 2.5rem;

  align-items: flex-start;
}

.ai-landing-launcher :deep(.choose-host .host-list) {
  margin-bottom: 1rem;
}

/* Align stepper, page title, section headings, and both columns to one shared left edge. */
.ai-landing-launcher :deep(.grid-section.container) {
  padding-left: var(--lw-inline-pad-x);
  padding-right: var(--lw-inline-pad-x);
}

/* Choose Host has no GridSection wrapper, so give its content the same inset by hand. */
.ai-landing-launcher :deep(.choose-host .host-controls) {
  padding-left: var(--lw-inline-pad-x);
  padding-right: var(--lw-inline-pad-x);
}

.ai-landing-launcher :deep(.choose-host .subtext) {
  padding-left: 0;
  padding-right: 0;
}

/* Billing has a single card with no wrapper padding of its own; inset it to match. */
.ai-landing-launcher :deep(.billing-period__card) {
  margin-left: var(--lw-inline-pad-x);
  margin-right: var(--lw-inline-pad-x);
}

.ai-landing-launcher :deep(.setup-controls-heading) {
  margin-left: 0;
}

.ai-landing-launcher :deep(.section-highlighted) {
  padding: 1.5rem;
  border-radius: 1.1rem;
}

.ai-landing-launcher :deep(.launcher-inline__footer .footer-actions) {
  max-width: 1360px;
  margin: 0 auto;
  padding-left: var(--lw-inline-pad-x);
  padding-right: var(--lw-inline-pad-x);
}

@media (width >= 1200px) {
  .ai-landing-launcher :deep(.launch-setup .grid__col-md-6),
  .ai-landing-launcher :deep(.review-setup .grid__col-md-6) {
    flex: 0 0 50%;
    max-width: 50%;
  }
}

/* Keep all host options on a single row instead of wrapping to a second line. */
@media (width >= 992px) {
  .ai-landing-launcher :deep(.choose-host .host-list) {
    flex-wrap: nowrap;
  }

  .ai-landing-launcher :deep(.choose-host .hosting-block) {
    flex: 1 1 0;
    min-width: 0;
    max-width: none;
  }
}

.ai-landing-launcher :deep(.progress-bar) {
  background: rgb(255, 255, 255, 8%);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 12;
}

/* Give the Setup / Billing / Review stepper the same left edge as the page title and section headings. */
.ai-landing-launcher :deep(.launcher-inline__progress) {
  padding: 1.5rem var(--lw-inline-pad-x) 0.5rem;
}

.ai-landing-launcher :deep(.launcher-inline__footer) {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 13;
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
  padding-top: var(--lw-footer-pad-y);
  padding-bottom: calc(var(--lw-footer-pad-y) + env(safe-area-inset-bottom, 0px));
  background: linear-gradient(
    180deg,
    rgb(43, 33, 62, 96%) 0%,
    rgb(27, 20, 42, 98%) 52%,
    rgb(19, 14, 30, 99%) 100%
  );
  border-top: 1px solid rgb(255, 255, 255, 12%);
}

.ai-landing-launcher :deep(.launcher-inline__footer > .text-link) {
  flex: 0 0 auto;
  margin-left: var(--lw-inline-pad-x);
  white-space: nowrap;
}

.ai-landing-launcher :deep(.launcher-inline__footer > .footer-actions) {
  margin-left: auto;
  width: auto;
  flex: 1 1 auto;
  justify-content: flex-end;
}

.ai-landing-launcher :deep(.launcher-inline__footer .footer-buttons) {
  margin-left: auto;
  width: auto;
  justify-content: flex-end;
}

@media (width <= 850px) {
  .ai-landing-launcher {
    --lw-footer-pad-y: 0.8rem;
  }

  .ai-landing-launcher :deep(.launcher-inline) {
    padding-bottom: 10.25rem;
  }

  .ai-landing-launcher :deep(.launcher-inline__footer) {
    gap: 0.65rem;
    flex-direction: column;
    align-items: stretch;
  }

  .ai-landing-launcher :deep(.launcher-inline__footer > .text-link) {
    margin-left: var(--lw-inline-pad-x);
    margin-right: var(--lw-inline-pad-x);
  }

  .ai-landing-launcher :deep(.launcher-inline__footer > .footer-actions) {
    margin-left: 0;
    width: 100%;
    justify-content: center;
  }

  .ai-landing-launcher :deep(.launcher-inline__footer .footer-actions) {
    align-items: stretch;
    text-align: left;
    padding-left: var(--lw-inline-pad-x);
    padding-right: var(--lw-inline-pad-x);
    gap: 0.5rem;
  }

  .ai-landing-launcher :deep(.launcher-inline__footer .footer-buttons) {
    margin-left: 0;
    width: 100%;
    justify-content: space-between;
  }
}

.ai-landing-launcher :deep(.section-highlighted),
.ai-landing-launcher :deep(.background-gradient--base),
.ai-landing-launcher :deep(.radio-block__inner) {
  background: rgb(255, 255, 255, 6%) !important;
  border: 1px solid rgb(255, 255, 255, 16%);
}

.ai-landing-launcher :deep(.radio-block--selected .radio-block__inner),
.ai-landing-launcher :deep(.host-selector-block--selected .host-selector-block__inner),
.ai-landing-launcher :deep(.billing-period__option--selected) {
  background: rgb(51, 230, 114, 12%) !important;
  border-color: #33e672;
  box-shadow: inset 0 0 0 1px rgb(51, 230, 114, 25%);
}

.ai-landing-launcher :deep(.host-setup--specific .section-highlighted),
.ai-landing-launcher :deep(.host-setup--specific .background-gradient--base) {
  background: linear-gradient(180deg, rgb(65 59 79 / 50%) 0%, #413b4f 91.78%) !important;
}

.ai-landing-launcher :deep(.section-highlighted),
.ai-landing-launcher :deep(.background-gradient--base),
.ai-landing-launcher :deep(.billing-period__card) {
  margin-top: 1rem;
}

.ai-landing-launcher :deep(.hero-heading),
.ai-landing-launcher :deep(.hero-subheading),
.ai-landing-launcher :deep(.setup-controls-heading),
.ai-landing-launcher :deep(.launch-heading--secondary),
.ai-landing-launcher :deep(.text-body),
.ai-landing-launcher :deep(.body-group),
.ai-landing-launcher :deep(.block-subtitle),
.ai-landing-launcher :deep(.subtext) {
  color: #fff;
}

.ai-landing-launcher :deep(.footer-price-sub),
.ai-landing-launcher :deep(.hosts-price),
.ai-landing-launcher :deep(.subtext p) {
  color: #c5bfd3;
}

@media (width <= 1023px) {
  .ai-landing-launcher {
    --lw-inline-pad-x: 1rem;
    --lw-hero-pad-y: 0.75rem;
    --lw-footer-pad-y: 0.85rem;

    padding: 2.5rem var(--lw-inline-pad-x) 3.5rem;
  }

  .ai-landing-launcher :deep(.section-highlighted) {
    padding: 1.35rem;
  }

  .ai-landing-launcher :deep(.app-launcher-hero .hero-masthead) {
    gap: 1rem;
  }

  .ai-landing-launcher :deep(.launcher-inline__footer .footer-actions) {
    padding-left: var(--lw-inline-pad-x);
    padding-right: var(--lw-inline-pad-x);
  }
}

@media (width <= 767px) {
  .ai-landing-launcher {
    --lw-inline-pad-x: 0.75rem;
    --lw-hero-pad-y: 0.5rem;
    --lw-footer-pad-y: 0.8rem;
    --lw-card-radius: 1.15rem;

    padding: 1.75rem var(--lw-inline-pad-x) 3rem;
  }

  .ai-landing-launcher :deep(.launcher-inline) {
    padding-bottom: 11.5rem;
  }

  .ai-landing-launcher :deep(.launcher-inline__progress) {
    padding: 1.25rem var(--lw-inline-pad-x) 0.5rem;
  }

  .ai-landing-launcher :deep(.app-launcher-hero) {
    padding-bottom: 0.5rem;
  }

  .ai-landing-launcher :deep(.launcher-inline__footer .footer-actions) {
    gap: 0.6rem;
  }

  .ai-landing-launcher :deep(.launcher-inline__footer) {
    flex-direction: column;
    align-items: stretch;
  }

  .ai-landing-launcher :deep(.launcher-inline__footer > .text-link) {
    margin-left: var(--lw-inline-pad-x);
  }

  .ai-landing-launcher :deep(.launcher-inline__footer > .footer-actions) {
    width: 100%;
  }
}
</style>
